import { Location, NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatError,
  MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent } from '@next/core-lib/components';
import { AgrNumberDirective } from '@next/core-lib/form';
import { Currency, LocaleService, CurrencySymbolPipe, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  Barn,
  Cycle,
  DeliveryOriginEnum,
  RouteTransitionBalance,
} from 'src/app/cycle/models/cycle.model';
import { CycleService } from 'src/app/cycle/services/cycle.service';
import { FinalSetupNavigationService } from 'src/app/cycle/services/final-setup-navigation.service';
import { FlockRegistrationService } from 'src/app/cycle/services/flock-registration.service';
import { getRouteTransitionBalance } from 'src/app/cycle/store/selectors/balance.selectors';
import { getCycles, getSelectedCycleById } from 'src/app/cycle/store/selectors/cycle.selectors';
import { getBarns } from 'src/app/settings/store/selectors/setting.selector';
import { PoultryContextKeys, VatPercentages } from 'src/app/shared/constants';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';
import Utils from 'src/app/shared/utils';

import * as cycleActions from '../../../cycle/store/actions/cycle.actions';
import { Delivery } from '../../models/delivery.model';
import {
  PostManualDeliveryRequest,
  ProductTypeEnum,
} from '../../models/flock-registration-service.model';
import { FlockRegistrationDeliveryService } from '../../services/flock-registration-delivery.service';
import { upsertManualDeliveryRequest } from '../../store/actions/delivery.actions';
import { getSelectedDelivery } from '../../store/selectors/delivery.selectors';

@Component({
  selector: 'app-delivery-item',
  templateUrl: './delivery-item.component.html',
  styles: [
    `
      .form-field-suffix {
        padding-left: 10px;
      }
      .form-field-prefix {
        padding-right: 10px;
      }
    `,
  ],
  imports: [
    PageDetailComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    NgIf,
    MatError,
    MatSelect,
    MatOption,
    NgFor,
    MatSlideToggle,
    AgrNumberDirective,
    MatPrefix,
    NgClass,
    MatButton,
    AsyncPipe,
    CurrencySymbolPipe,
    TranslatePipe,
  ],
})
export class DeliveryItemComponent implements OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private localeService = inject(LocaleService);
  private location = inject(Location);
  private flockRegistrationDeliveryService = inject(FlockRegistrationDeliveryService);
  private flockRegistrationService = inject(FlockRegistrationService);
  private cycleService = inject(CycleService);
  private settingsHelper = inject(SettingsServiceHelper);
  private finalSetupNavigationService = inject(FinalSetupNavigationService);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  pageTitle$: Observable<string> = this.store.pipe(
    withLatestFrom(this.routeUrl$),
    map(
      ([_, url]: [object, string]) =>
        this.localeService.translate(url.endsWith('/delivery/add') ? 'add' : 'edit') +
        ' ' +
        this.localeService.translate('delivery-item.manual-delivery'),
    ),
  );

  maxDeliveryDate$: Observable<Date> = this.store.select(getCycles).pipe(
    take(1),
    map((cycles) => {
      if (cycles.length > 0) {
        const cycle = cycles.find((c) => c.id === this.cycleId);
        return cycle?.cycleEndDate ? new Date(cycle?.cycleEndDate) : new Date();
      }
    }),
  );

  deliveryGroup = new FormGroup({
    deliveryDate: new FormControl(null, [Validators.required]),
    productTitle: new FormControl(null, [Validators.required]),
    productType: new FormControl(true, null),
    externalProductId: new FormControl(null, [Validators.maxLength(100)]),
    unitsDelivered: new FormControl(null, [
      Validators.required,
      (control: FormGroup): ValidationErrors | null => {
        if (this.manualDelivery === undefined || this.manualDelivery === null) {
          return null;
        }
        const unitsDelivered: number = parseFloat(<string>control.value);
        const unitsConsumed =
          this.manualDelivery.deliveryLines[0].kgDelivered -
          this.manualDelivery.deliveryLines[0].unitsRemaining;
        let positive = false;
        if (unitsConsumed >= 0) {
          positive = true;
        }

        if (unitsConsumed === 0) {
          return null;
        }

        if (positive && unitsConsumed > unitsDelivered) {
          return { notEnough: unitsConsumed };
        }

        if (!positive && unitsConsumed < unitsDelivered) {
          return { notEnough: unitsConsumed };
        }

        return null;
      },
    ]),
    amounts: new FormControl(true, [Validators.required]),
    price: new FormControl(null, [Validators.required]),
    pricePer100kg: new FormControl(null, [Validators.required]),
    barnId: new FormControl(null),
    externalInvoiceId: new FormControl<string>(null, [Validators.maxLength(50)]),
    invoiceDate: new FormControl<string>(Utils.toUtcDate(new Date()).toISOString(), []),
    vatPercentage: new FormControl<number>(null, []),
    showInFinalSetup: new FormControl<boolean>(true, []),
  });

  unitVolumeType = 'kg';
  cycleId?: number;
  manualDelivery$: Observable<Delivery> = this.store.select(getSelectedDelivery);
  manualDelivery: Delivery;
  manualDeliveryId?: number;
  currency: Currency;

  barns$: Observable<Barn[]> = this.store.select(getBarns);
  barnOptions$: Observable<Barn[]>;
  selectedBarn: Barn;
  dayOldChickTotals: Array<{ title: string; value: number | string; col_width: number }>;
  hasDayOldChicks: boolean;
  subscription: Subscription;
  vatPercentages = VatPercentages;

  isFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();
  isFinalSetup: boolean;

  public saveBtnDisabled = false;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const activatedRoute = this.activatedRoute;

    // Check any parameters that are coming from the cycle balance screen for adding a
    // new manual delivery.
    const routeParams = this.router.lastSuccessfulNavigation()?.extras;
    this.cycleId = routeParams?.state?.cycleId
      ? (<{ [p: string]: number }>routeParams.state).cycleId
      : null;
    this.manualDeliveryId = activatedRoute.snapshot.params['deliveryId'] as number;

    this.createBarnOptions();

    this.isFinalSetup$
      .pipe(take(1))
      .subscribe((isFinalSetup) => (this.isFinalSetup = isFinalSetup));
  }

  unsubscribeSignal: Subject<void> = new Subject<void>();

  ngOnInit() {
    this.currency = this.localeService.getLocationCurrency();

    this.deliveryGroup.controls.amounts.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((amounts: boolean) => {
        if (amounts) {
          this.deliveryGroup.controls.price.setValidators(Validators.required);
          this.deliveryGroup.controls.price.updateValueAndValidity();
          this.deliveryGroup.controls.pricePer100kg.setValidators(Validators.required);
          this.deliveryGroup.controls.pricePer100kg.updateValueAndValidity();
        } else {
          this.deliveryGroup.controls.price.patchValue(null);
          this.deliveryGroup.controls.price.clearValidators();
          this.deliveryGroup.controls.price.updateValueAndValidity();
          this.deliveryGroup.controls.pricePer100kg.patchValue(null);
          this.deliveryGroup.controls.pricePer100kg.clearValidators();
          this.deliveryGroup.controls.pricePer100kg.updateValueAndValidity();
        }
      });

    if (this.manualDeliveryId) {
      this.manualDelivery$.pipe(take(1)).subscribe((delivery) => {
        this.manualDelivery = delivery;
        if (this.manualDelivery !== undefined) {
          if (delivery.deliveryLines.length > 0) {
            this.deliveryGroup.controls.deliveryDate.setValue(delivery.deliveryDate);
            this.deliveryGroup.controls.productTitle.setValue(
              delivery.deliveryLines[0].productTitle,
            );
            this.deliveryGroup.controls.productType.setValue(
              delivery.deliveryLines[0].productType === ProductTypeEnum.MIXEDPRODUCT,
            );
            this.deliveryGroup.controls.externalProductId.setValue(
              delivery.deliveryLines[0].externalProductId,
            );
            this.deliveryGroup.controls.unitsDelivered.setValue(
              delivery.deliveryLines[0].unitsDelivered,
            );
            if (delivery.deliveryLines[0].unitNettoPrice) {
              this.deliveryGroup.controls.price.setValue(
                delivery.deliveryLines[0].unitNettoPrice * delivery.deliveryLines[0].unitsDelivered,
              );
              this.deliveryGroup.controls.pricePer100kg.setValue(
                delivery.deliveryLines[0].unitNettoPrice * 100,
              );
            }
            this.deliveryGroup.controls.amounts.setValue(
              delivery.deliveryLines[0].unitNettoPrice != null,
            );
            this.deliveryGroup.controls.barnId.setValue(delivery.deliveryLines[0].barnId);
            this.deliveryGroup.controls.invoiceDate.setValue(delivery.deliveryLines[0].invoiceDate);
            this.deliveryGroup.controls.externalInvoiceId.setValue(
              delivery.deliveryLines[0].externalInvoiceId,
            );
            this.deliveryGroup.controls.vatPercentage.setValue(
              delivery.deliveryLines[0].vatPercentage,
            );
            this.deliveryGroup.controls.showInFinalSetup.setValue(
              delivery.deliveryLines[0].showInFinalSetup,
            );
          }
        }
      });
    }

    this.deliveryGroup.controls.unitsDelivered.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((unitsDelivered: number) => {
        if (unitsDelivered !== null && this.deliveryGroup.controls.pricePer100kg.value !== null) {
          this.deliveryGroup.controls.price.patchValue(
            (unitsDelivered / 100) * <number>this.deliveryGroup.controls.pricePer100kg.value,
            {
              emitEvent: false,
              onlySelf: true,
            },
          );
        }
      });

    this.deliveryGroup.controls.price.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((price: number) => {
        if (price !== null && this.deliveryGroup.controls.unitsDelivered.value !== null) {
          this.deliveryGroup.controls.pricePer100kg.patchValue(
            (price / <number>this.deliveryGroup.controls.unitsDelivered.value) * 100,
            {
              emitEvent: false,
              onlySelf: true,
            },
          );
        }
      });

    this.deliveryGroup.controls.pricePer100kg.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((pricePer100kg: number) => {
        if (pricePer100kg !== null && this.deliveryGroup.controls.unitsDelivered.value !== null) {
          this.deliveryGroup.controls.price.patchValue(
            (pricePer100kg / 100) * <number>this.deliveryGroup.controls.unitsDelivered.value,
            {
              emitEvent: false,
              onlySelf: true,
            },
          );
        }
      });
  }

  changeBarnSelection(barnId: number) {
    this.barns$.pipe(take(1)).subscribe((barns) => {
      const foundBarn = barns.find((barn) => barn.id === Number(barnId));
      if (foundBarn) {
        this.selectedBarn = foundBarn;
        this.settingsHelper.setSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID, foundBarn.id);
      } else {
        this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);
        this.selectedBarn = null;
      }
    });
  }

  private createBarnOptions() {
    const selectedCycle$ = this.store.select(getSelectedCycleById(this.cycleId));
    this.barnOptions$ = combineLatest([selectedCycle$, this.barns$]).pipe(
      map(([cycle, barns]) => {
        if (cycle?.id) {
          const hasDayOldChicks = cycle.dayOldChicks?.length > 0;
          const newBarnOptions: Barn[] = [];

          if (!hasDayOldChicks) {
            return newBarnOptions;
          }

          barns.forEach((barn) => {
            const barnInArrival =
              cycle.dayOldChicks.findIndex((dayoldchick) => dayoldchick.barn.id === barn.id) !== -1;

            if (barnInArrival) {
              newBarnOptions.push(barn);
            }
          });

          return newBarnOptions;
        }
      }),
      tap((barnOptions) => {
        if (barnOptions.length === 1) {
          this.deliveryGroup.controls.barnId.setValue(barnOptions[0].id);
        }
      }),
    );
  }

  back(saved = false) {
    if (this.cycleId && saved) {
      // Simply go back to the previous page, which is the balance screen of the cycle.
      this.location.back();
    } else if (this.router.navigated) {
      this.location.back();
    } else {
      this.router.navigate(['../'], { relativeTo: this.activatedRoute });
    }
  }

  save() {
    if (this.deliveryGroup.valid) {
      this.saveBtnDisabled = true;

      const unitsDelivered = parseFloat(<string>this.deliveryGroup.controls.unitsDelivered.value);
      let manualDeliveryRequest: PostManualDeliveryRequest = {
        id: 0,
        deliveryDate: Utils.parseDateTimeFromFormValue(
          this.deliveryGroup.controls.deliveryDate.value,
        ),
        manualDeliveryLines: [
          {
            id: '1',
            externalUniqueIdentifier: '1',
            currency: this.currency.toString(),
            externalProductId: <string>this.deliveryGroup.controls.externalProductId.value,
            productTitle: <string>this.deliveryGroup.controls.productTitle.value,
            productType:
              this.deliveryGroup.controls.productType.value === true
                ? ProductTypeEnum.MIXEDPRODUCT
                : ProductTypeEnum.RAWPRODUCT,
            unitVolumeType: this.unitVolumeType,
            unitNettoPrice:
              parseFloat(<string>this.deliveryGroup.controls.price.value) / unitsDelivered,
            unitsDelivered,
            isActive: true,
            kgDelivered: unitsDelivered,
            barnId: this.deliveryGroup.controls.barnId.value,
            invoiceDate: Utils.parseDateTimeFromFormValue(
              this.deliveryGroup.controls.invoiceDate.value,
            ),
            externalInvoiceId: this.deliveryGroup.controls.externalInvoiceId.value,
            vatPercentage: this.deliveryGroup.controls.vatPercentage.value,
            showInFinalSetup: this.deliveryGroup.controls.showInFinalSetup.value,
          },
        ],
      };

      if (this.manualDelivery !== undefined) {
        manualDeliveryRequest = {
          ...manualDeliveryRequest,
          id: this.manualDelivery.id,
          manualDeliveryLines: [
            {
              ...this.manualDelivery.deliveryLines[0],
              currency: this.currency.toString(),
              externalProductId: <string>this.deliveryGroup.controls.externalProductId.value,
              productTitle: <string>this.deliveryGroup.controls.productTitle.value,
              productType:
                this.deliveryGroup.controls.productType.value === true
                  ? ProductTypeEnum.MIXEDPRODUCT
                  : ProductTypeEnum.RAWPRODUCT,
              unitVolumeType: this.unitVolumeType,
              unitNettoPrice:
                parseFloat(<string>this.deliveryGroup.controls.price.value) / unitsDelivered,
              unitsDelivered,
              kgDelivered: unitsDelivered,
              barnId: this.deliveryGroup.controls.barnId.value,
              externalInvoiceId: this.deliveryGroup.controls.externalInvoiceId.value,
              invoiceDate: Utils.parseDateTimeFromFormValue(
                this.deliveryGroup.controls.invoiceDate.value,
              ),
              vatPercentage: this.deliveryGroup.controls.vatPercentage.value,
              showInFinalSetup: this.deliveryGroup.controls.showInFinalSetup.value,
            },
          ],
        };
      }

      if (this.cycleId) {
        this.flockRegistrationDeliveryService
          .upsertManualDelivery(manualDeliveryRequest)
          .pipe(
            withLatestFrom(this.store.select(getRouteTransitionBalance)),
            tap(([manualDeliveryId, routeTransitionBalance]) => {
              this.flockRegistrationService
                .calculateBalanceDraft(this.cycleId)
                .pipe(
                  take(1),
                  tap((response: Cycle | HttpErrorResponse) => {
                    const cycle = response as Cycle;
                    const draftBalanceTableRows = this.cycleService.transformBalanceRows(
                      cycle.balancesPerDeliveryLine,
                      cycle,
                    );
                    const correspondingBalanceTableRow = draftBalanceTableRows.find(
                      (b) => b.deliveryId === manualDeliveryId,
                    );
                    const correspondingDeliveryUsed = cycle.balancesPerDeliveryLine.find(
                      (d) =>
                        d.deliveryId === manualDeliveryId &&
                        d.deliverySource === DeliveryOriginEnum.MANUAL,
                    );
                    if (correspondingBalanceTableRow && correspondingDeliveryUsed) {
                      // Add the added manual delivery to the pending balance in the front-end.
                      const updatedRouteTransitionBalance: RouteTransitionBalance = {
                        balanceRows: [
                          ...routeTransitionBalance.balanceRows,
                          correspondingBalanceTableRow,
                        ],
                      };
                      this.store.dispatch(cycleActions.setBalancePendingChanges());
                      this.store.dispatch(
                        cycleActions.setRouteTransitionBalance({
                          routeTransitionBalance: updatedRouteTransitionBalance,
                        }),
                      );

                      this.back(true);
                    } else {
                      this.back(false);
                    }
                  }),
                )
                .subscribe();
            }),
          )
          .subscribe();
      } else {
        this.store.dispatch(
          upsertManualDeliveryRequest({
            manualDeliveryRequest,
          }),
        );

        this.back(true);
      }
    }
  }
}
